import googleIcon from '@/assets/logos/google-color.svg';
import { KINDE_GMAIL_CONNECTION_ID } from '@/components/constants/kinde';
import { iframeSafeAuthRoute } from '@/components/modal/sign-in-modal/utils';
import { signInClicked } from '@/lib/analytics/scribe/auth';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import { beginDesktopKindeAttempt } from '@/tauri/auth0/login';
import { useDesktopLoginPending } from '@/tauri/auth0/use-desktop-login-pending';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { isAuth0SocialLoginGoogleAllowed } from '@/utils/auth/isAuth0SocialLoginGoogleAllowed';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@/utils/translations';
import { AUTH0_GOOGLE_CONNECTION, AUTH0_LOGIN_PATH } from '@heidi/auth/contracts';
import { Button } from '@heidi/design-system/button';
import { Loader } from '@heidi/design-system/loader';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import Image from 'next/image';
import { useState } from 'react';

interface GoogleLoginButtonProps {
  /** Callback when button is clicked (before auth redirect) */
  onClick?: () => void;
  /** Callback when desktop auth login fails before redirect completes */
  onLoginFailed?: () => void;
  /** External disabled state (e.g., when another button is loading) */
  disabled?: boolean;
  /** Full redirect URI including locale prefix */
  postLoginRedirectUri: string;
  /** Button size. @default 'lg' */
  size?: 'sm' | 'md' | 'lg' | 'touch';
  /**
   * Overrides the locale used for Auth0 `ui_locales` / Kinde `lang`. Pre-auth
   * desktop flows must pass the react-intl locale here — `useTranslations()`
   * otherwise falls back to `router.locale` (always `en` on desktop).
   */
  localeOverride?: string;
}

/**
 * Google social login button that uses Auth0 unless the build forces Kinde.
 */
export function GoogleLoginButton({
  onClick,
  onLoginFailed,
  disabled,
  postLoginRedirectUri,
  size = 'lg',
  localeOverride,
}: GoogleLoginButtonProps) {
  const { t, locale } = useTranslations({ localeOverride });
  const { login: kindeLogin } = useKindeAuth();
  const { login: desktopAuthLogin } = useDesktopAuthSafe();
  const shouldUseDesktopAuth = isTauri();
  const [isLoading, setIsLoading] = useState(false);

  // Desktop auth resolves in-place (no redirect): hold the loading state from
  // the click until the attempt settles (session, auth error, launch failure,
  // or the 5s abandonment watchdog), so a second click can never start a
  // second PKCE attempt while the browser leg runs (MUL-3346). The shared
  // pending flag also disables sibling sign-in buttons on every surface.
  const isDesktopLoginPending = useDesktopLoginPending(() => setIsLoading(false));
  const isDisabled = disabled || isLoading || isDesktopLoginPending;

  // Kinde branches: exact pre-MUL-3346 semantics (spinner on click; local
  // loading disables on web only — on web a Kinde click really does navigate
  // the page away, so there is nothing left to unlock; on desktop the click is
  // marked pending via beginDesktopKindeAttempt so the shared flag governs it) plus exclusion while a
  // genuine desktop attempt (shared pending) is in flight. This deliberately
  // differs from DBLoginButton's Kinde guard (isLoading disables everywhere):
  // each button keeps ITS OWN pre-PR expression, and Kinde clicks never mint
  // a PKCE attempt, so the MUL-3346 single-slot hazard does not apply here.
  const isKindeDisabled =
    disabled || (!shouldUseDesktopAuth && isLoading) || isDesktopLoginPending;

  const buildAuth0GoogleLoginUrl = () => {
    const params = new URLSearchParams();
    params.set('returnTo', postLoginRedirectUri);
    params.set('connection', AUTH0_GOOGLE_CONNECTION);
    if (locale) {
      params.set('ui_locales', toAuth0UiLocales(locale));
    }
    return `${AUTH0_LOGIN_PATH}?${params.toString()}`;
  };

  const handleClick = () => {
    signInClicked('google');
    setIsLoading(true);
    onClick?.();
  };

  const googleIconNode = (
    <Image src={googleIcon} loading="lazy" alt="google logo" className="size-4.5" />
  );

  if (isAuth0SocialLoginGoogleAllowed()) {
    return (
      <Button
        disableAutomaticSvgSizing
        className="w-full"
        variant="secondary"
        size={size}
        disabled={isDisabled}
        type="button"
        onClick={() => {
          handleClick();
          if (shouldUseDesktopAuth) {
            void desktopAuthLogin({
              connection: AUTH0_GOOGLE_CONNECTION,
              uiLocales: toAuth0UiLocales(locale),
            })
              .then((launched) => {
                // Positive signal from login(): `false` = the call was a
                // no-op (outside DesktopAuthProvider / bridge unavailable),
                // so neither .catch nor a pending falling edge will ever
                // release this button or the surface's shared in-progress
                // state. NOT inferred from pending-state absence — that is
                // also the post-settle state, and a fast settle must not
                // surface a false failure (MUL-3346 review).
                if (!launched) {
                  setIsLoading(false);
                  onLoginFailed?.();
                }
              })
              .catch((error) => {
                logger.error('[Auth0] Desktop Google login failed', error);
                // The launch never marked an attempt pending (or already
                // cleared it), so release the spinner here.
                setIsLoading(false);
                onLoginFailed?.();
              });
            return;
          }
          const loginUrl = buildAuth0GoogleLoginUrl();
          iframeSafeAuthRoute(loginUrl);
        }}
      >
        {isLoading ? <Loader className="size-4.5" /> : googleIconNode}
        {t('auth.continueWithGoogle')}
      </Button>
    );
  }

  // Kinde path - React Kinde (Button)
  if (isUsingReactKinde()) {
    return (
      <Button
        disableAutomaticSvgSizing
        className="w-full"
        variant="secondary"
        size={size}
        disabled={isKindeDisabled}
        type="button"
        onClick={() => {
          handleClick();
          // Desktop: this hands off to the system browser and never comes
          // back with a settle signal — mark the attempt so the abandonment
          // watchdog releases the spinner and the surface's shared disable.
          beginDesktopKindeAttempt();
          void kindeLogin({
            authUrlParams: {
              post_login_redirect_url: postLoginRedirectUri,
              connection_id: KINDE_GMAIL_CONNECTION_ID,
              lang: locale,
            },
            app_state: {
              redirectTo: postLoginRedirectUri,
            },
          });
        }}
      >
        {isLoading ? <Loader className="size-4.5" /> : googleIconNode}
        {t('auth.continueWithGoogle')}
      </Button>
    );
  }

  // Kinde google
  return (
    <Button
      disableAutomaticSvgSizing
      className="w-full"
      variant="secondary"
      size={size}
      disabled={isKindeDisabled}
      type="button"
      onClick={() => {
        handleClick();
        const url = new URL('/api/auth/login', window.location.origin);
        url.searchParams.set('post_login_redirect_url', postLoginRedirectUri);
        url.searchParams.set('connection_id', KINDE_GMAIL_CONNECTION_ID);
        iframeSafeAuthRoute(url.toString());
      }}
    >
      {isLoading ? <Loader className="size-4.5" /> : googleIconNode}
      {t('auth.continueWithGoogle')}
    </Button>
  );
}
